import React from "react";
import {
    MdRestaurant,
    MdDeliveryDining,
    MdCreditCard,
    MdLocalShipping,
} from "react-icons/md";
import {
    FaShoppingBag,
    FaMoneyBillWave,
    FaUser,
    FaTable,
    FaStoreAlt,
} from "react-icons/fa";
import { IoTicket, IoStorefront } from "react-icons/io5";
import { IoMdNotifications } from "react-icons/io";

interface IconRule {
    keywords: string[];
    icon: React.ReactNode;
}

const SALE_MODE_RULES: IconRule[] = [
    {
        keywords: ["sur place", "on-site", "on site", "dine-in", "dine in"],
        icon: <MdRestaurant size={64} />,
    },
    {
        keywords: ["emporter", "takeaway", "take away", "take-away", "à emporter"],
        icon: <FaShoppingBag size={56} />,
    },
    {
        keywords: ["livraison", "delivery", "en livraison"],
        icon: <MdDeliveryDining size={64} />,
    },
    {
        keywords: ["retrait", "click", "collect", "c&c", "drive"],
        icon: <FaStoreAlt size={56} />,
    },
    {
        keywords: ["expédition", "expedition", "shipping", "envoi"],
        icon: <MdLocalShipping size={56} />,
    },
];

const PAYMENT_MODE_RULES: IconRule[] = [
    {
        keywords: ["carte", "credit", "card", "cb", "bancaire", "visa", "mastercard"],
        icon: <MdCreditCard size={64} />,
    },
    {
        keywords: ["espèce", "espece", "cash", "monnaie", "liquide"],
        icon: <FaMoneyBillWave size={56} />,
    },
    {
        keywords: ["ticket", "restaurant", "meal", "voucher", "chèque", "cheque", "déjeuner"],
        icon: <IoTicket size={56} />,
    },
    {
        keywords: ["en ligne", "online", "internet", "web"],
        icon: <IoStorefront size={56} />,
    },
];

const INFO_MODE_RULES: IconRule[] = [
    {
        keywords: [
            "table", "numéro de table", "table number",
            "table service", "numero de table", "n° de table",
        ],
        icon: <FaTable size={56} />,
    },
    {
        keywords: [
            "nom", "name", "client", "customer",
            "nom du client", "customer name", "prénom",
        ],
        icon: <FaUser size={56} />,
    },
    {
        keywords: [
            "buzzer", "bipeur", "notification", "sonner", "sonnette",
            "beeper", "pager", "vibreur",
        ],
        icon: <IoMdNotifications size={64} />,
    },
    {
        keywords: [
            "ticket", "numéro", "number", "ordre", "order",
            "numéro de commande", "order number", "n° commande",
        ],
        icon: <IoTicket size={56} />,
    },
];

function matchIcon(
    item: { name?: string; displayName?: { FR?: string; EN?: string } },
    rules: IconRule[]
): React.ReactNode | undefined {
    const searchTexts = [
        item.name || "",
        item.displayName?.FR || "",
        item.displayName?.EN || "",
    ]
        .join(" ")
        .toLowerCase();

    for (const rule of rules) {
        if (rule.keywords.some((kw) => searchTexts.includes(kw))) {
            return rule.icon;
        }
    }
    return undefined;
}

export function getSaleModeIcon(item: {
    name?: string;
    displayName?: { FR?: string; EN?: string };
}): React.ReactNode | undefined {
    return matchIcon(item, SALE_MODE_RULES);
}

export function getPaymentModeIcon(item: {
    name?: string;
    displayName?: { FR?: string; EN?: string };
}): React.ReactNode | undefined {
    return matchIcon(item, PAYMENT_MODE_RULES);
}

export function getInfoModeIcon(item: {
    name?: string;
    displayName?: { FR?: string; EN?: string };
}): React.ReactNode | undefined {
    return matchIcon(item, INFO_MODE_RULES);
}

export function getLanguageIcon(item: {
    id?: string;
    flag?: string;
    title?: string;
    name?: string;
}): React.ReactNode {
    if (item.flag) {
        return (
            <img
                src={item.flag}
                alt={item.title || item.name}
                className="pos-language-flag__clz"
                data-testid={`pos-language-flag-${item.id}`}
            />
        );
    }
    return (
        <span className="pos-language-fallback-flag__clz">
            🏳
        </span>
    );
}
